import { ILang } from "../i18n/ILang";
import { menuWithLang } from "../Menu/data";
import { IMenuItem } from "../Menu/IMenu";
import { IUser } from "../user/IUser";

export type OnLoginRequestCallbackType = (username: string, password: string) => Promise<IUser>;
export type OnLogoutRequestCallbackType = () => Promise<void>;
export type OnAnalyticsRequestCallbackType = (label: string) => void;

export type NavState = {
  lang: ILang;
  isMenuOpen: boolean;
  isSearchOpen: boolean;
  isUserMenuOpen: boolean;
  user?: IUser;
  shoppingListNumItems: number;
  menuItems: {
    all: IMenuItem[];
    freeTrial: IMenuItem;
    becomeMemberPayment: IMenuItem;
    login: IMenuItem;
    logout: IMenuItem;
    myAccount: IMenuItem;
    shoppingList: IMenuItem;
    tracking: IMenuItem;
    appCta: IMenuItem;
    stores: IMenuItem[];
  };
  hasAdminBar: boolean;
  onLoginRequestCallback?: OnLoginRequestCallbackType;
  onLogoutRequestCallback?: OnLogoutRequestCallbackType;
  onAnalyticsRequestCallback?: OnAnalyticsRequestCallbackType;
};

export const initialState: NavState = {
  lang: ILang.en,
  isMenuOpen: false,
  isSearchOpen: false,
  isUserMenuOpen: false,
  shoppingListNumItems: 0,
  menuItems: menuWithLang(ILang.en),
  hasAdminBar: false
};

export enum NavActionType {
  "SET_SHOPPING_LIST_NUM_ITEMS",
  "OPEN_USER_MENU",
  "CLOSE_USER_MENU",
  "OPEN_SEARCH",
  "CLOSE_SEARCH",
  "OPEN_MENU",
  "CLOSE_MENU",
  "SET_LOGGED_IN",
  "SET_LOGGED_OUT",
  "SET_LANGUAGE"
}

export type NavAction = {
  type: NavActionType;
};

export type NavActionLoggedIn = {
  type: NavActionType.SET_LOGGED_IN;
  user: IUser;
};

export type NavActionSetLanguage = {
  type: NavActionType.SET_LANGUAGE;
  lang: ILang;
};

export type NavActionSetShoppingListNumItems = {
  type: NavActionType.SET_SHOPPING_LIST_NUM_ITEMS;
  numItems: number;
};

export function reducer(state: NavState, action: NavAction): NavState {
  switch (action.type) {
    case NavActionType.OPEN_MENU:
      return {
        ...state,
        isMenuOpen: true,
        isSearchOpen: false,
        isUserMenuOpen: false
      };
    case NavActionType.CLOSE_MENU:
      return { ...state, isMenuOpen: false };
    case NavActionType.OPEN_SEARCH:
      return {
        ...state,
        isSearchOpen: true,
        isMenuOpen: false,
        isUserMenuOpen: false
      };
    case NavActionType.CLOSE_SEARCH:
      return { ...state, isSearchOpen: false };
    case NavActionType.OPEN_USER_MENU:
      return {
        ...state,
        isSearchOpen: false,
        isMenuOpen: false,
        isUserMenuOpen: true
      };
    case NavActionType.CLOSE_USER_MENU:
      return { ...state, isUserMenuOpen: false };
    case NavActionType.SET_LOGGED_IN:
      const user = (action as NavActionLoggedIn).user; // eslint-disable-line no-case-declarations
      return { ...state, user };
    case NavActionType.SET_LOGGED_OUT:
      return { ...state, user: undefined };
    case NavActionType.SET_LANGUAGE: {
      const { lang } = action as NavActionSetLanguage;
      return {
        ...state,
        lang,
        menuItems: menuWithLang(lang)
      };
    }
    case NavActionType.SET_SHOPPING_LIST_NUM_ITEMS:
      return {
        ...state,
        shoppingListNumItems: (action as NavActionSetShoppingListNumItems).numItems
      };
    default:
      throw new Error();
  }
}

export const openMenu = (): NavAction => ({
  type: NavActionType.OPEN_MENU
});

export const closeMenu = (): NavAction => ({
  type: NavActionType.CLOSE_MENU
});

export const openSearch = (): NavAction => ({
  type: NavActionType.OPEN_SEARCH
});

export const closeSearch = (): NavAction => ({
  type: NavActionType.CLOSE_SEARCH
});

export const openUserMenu = (): NavAction => ({
  type: NavActionType.OPEN_USER_MENU
});

export const closeUserMenu = (): NavAction => ({
  type: NavActionType.CLOSE_USER_MENU
});

export const setUserLoggedIn = (user: IUser): NavActionLoggedIn => ({
  type: NavActionType.SET_LOGGED_IN,
  user
});

export const setUserLoggedOut = (): NavAction => ({
  type: NavActionType.SET_LOGGED_OUT
});

export const setLanguage = (lang: ILang): NavActionSetLanguage => ({
  type: NavActionType.SET_LANGUAGE,
  lang
});

export const setShoppingListNumItems = (numItems: number): NavActionSetShoppingListNumItems => ({
  type: NavActionType.SET_SHOPPING_LIST_NUM_ITEMS,
  numItems
});
